import { useContext, useEffect, useMemo } from 'react';
import { ToastContext } from './ToastContext';
import type { ToastAction } from './ToastContext.types';

export const useToastContext = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToastContext must be used within ToastProvider');
  return ctx;
};

export const useToast = () => {
  const { addToast } = useToastContext();

  return useMemo(
    () => ({
      success: (message: string, duration?: number, action?: ToastAction) =>
        addToast(message, 'success', duration, action),
      error: (message: string, duration?: number, action?: ToastAction) =>
        addToast(message, 'error', duration, action),
      warning: (message: string, duration?: number, action?: ToastAction) =>
        addToast(message, 'warning', duration, action),
      info: (message: string, duration?: number, action?: ToastAction) =>
        addToast(message, 'info', duration, action),
    }),
    [addToast],
  );
};

/**
 * Shifts the global toast stack `offset` px further left while this
 * component is mounted with a non-zero value -- e.g. clear of a right-docked
 * panel that would otherwise sit on top of it. Resets to 0 on unmount, or
 * whenever `offset` itself changes, so leaving the page (or closing the
 * panel) never leaves some other screen's toasts shifted for no reason.
 */
export const useToastRightOffset = (offset: number) => {
  const { setToastRightOffset } = useToastContext();

  useEffect(() => {
    setToastRightOffset(offset);
    return () => setToastRightOffset(0);
  }, [offset, setToastRightOffset]);
};
